// Estimation of the first contentful paint (FCP) time.
export const estimatedFCP = 1000;
const startPerf = self?.performance?.now() || estimatedFCP;
const startDate = Date.now();

/**
 * Returns the result of the performance.now() method or an approximation of it.
 * @return {number} The elapsed time since the page was requested.
 */
export const now = () => {
	return self?.performance?.now() || (Date.now() - startDate + startPerf);
};

/**
 * Throttles a function and delays its execution, so it's only called at most
 * once within a given time period.
 * @param {Function} fn The function to throttle.
 * @param {number} timeout The amount of time that must pass before the
 *     function can be called again.
 * @return {[Function, Function]} The throttled function and a function to
 * 	 cancel the throttling.
 */
export const throttleCancelable = (fn, timeout) => {
	let timer = null;
	return [
		(...args) => {
			if (!timer) {
				timer = setTimeout(() => {
					fn(...args);
					timer = null;
				}, timeout);
			}
		},
		() => {
			timer && clearTimeout(timer);
			timer = null;
		},
	];
};
export const throttle = (fn, timeout) => {
	return throttleCancelable(fn, timeout)[0];
};

/**
 * Debounces a function, so it's only called after a given time period has
 * passed since the last time it was called.
 * @param {Function} fn The function to debounce.
 * @param {number} timeout The amount of time that must pass before the
 *     function can be called again.
 * @return {[Function, Function]} The debounced function and a function to
 * 	 cancel the debouncing.
 */
export const debounceCancelable = (fn, timeout) => {
	let timer = null;
	return [
		(...args) => {
			timer && clearTimeout(timer);
			timer = setTimeout(() => {
				fn(...args);
				timer = null;
			}, timeout);
		},
		() => {
			timer && clearTimeout(timer);
			timer = null;
		},
	];
};
export const debounce = (fn, timeout) => {
	return debounceCancelable(fn, timeout)[0];
};